import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  cohortQueryKeys,
  fetchCohortVersion,
  useCohortListQuery,
} from '@verantos-dev/data-access-cohorts';
import type { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { selectCohorts } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type {
  CohortPickerOption,
  UseAddCohortPickerModalArgs,
  UseAddCohortPickerModalResult,
} from '../types';
import { useAnalyticalDatasetDetailMetaRow } from './useAnalyticalDatasetDetailMetaRow';

export const useAddCohortPickerModal = ({
  projectId,
  onClose,
}: UseAddCohortPickerModalArgs): UseAddCohortPickerModalResult => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const storeCohorts = useAnalyticalDatasetDefinitionStore(selectCohorts);
  const addCohort = useAnalyticalDatasetDefinitionStore((s) => s.addCohort);
  const removeCohort = useAnalyticalDatasetDefinitionStore((s) => s.removeCohort);

  const currentCohortId = storeCohorts[0]?.cohortProfileId ?? null;

  const { data: cohortList, isLoading, isError } = useCohortListQuery({ projectId });

  // A non-PROCESSED cohort (DRAFT, PROCESSING, ERROR, FINAL) may be added to
  // an AD too — AD processing is blocked server-side until every referenced
  // cohort is PROCESSED (the process gate, not this picker, enforces that).
  // UNSAVED cohorts are staged drafts, not real library cohorts, so never
  // selectable regardless of the flag.
  // TODO: cleanup with asset_edit_while_in_use ff
  const allowDraftCohorts = Boolean(useFeatureFlagSplit('asset_edit_while_in_use'));

  // Only cohorts on this AD's dataset can be combined with it: processing
  // resolves the run's dataset from the cohort and materializes every variable
  // against that one, so a cohort from another dataset would be dropped rather
  // than merged. The server refuses the same pairing — this keeps the offer
  // honest instead of letting the save fail.
  const { datasetReleaseId: adDatasetReleaseId } = useAnalyticalDatasetDetailMetaRow();

  const allOptions = useMemo<CohortPickerOption[]>(() => {
    if (!cohortList) return [];
    return cohortList
      .filter((c) => {
        if (adDatasetReleaseId && c.latestDatasetReleaseId) {
          if (c.latestDatasetReleaseId !== adDatasetReleaseId) return false;
        }
        if (allowDraftCohorts) {
          return true;
        }
        return c.latestStatus === CohortDefinitionStatus.PROCESSED && c.latestSubjectCount > 0;
      })
      .map((c) => ({
        cohortProfileId: c.cohortProfileId,
        cohortName: c.cohortName,
        latestStatus: c.latestStatus,
        latestSubjectCount: c.latestSubjectCount,
      }));
  }, [cohortList, allowDraftCohorts, adDatasetReleaseId]);

  const [searchQuery, setSearchQuery] = useState('');
  const onSearchChange = useCallback((next: string) => setSearchQuery(next), []);

  const options = useMemo<CohortPickerOption[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.cohortName.toLowerCase().includes(q));
  }, [allOptions, searchQuery]);

  // One-shot seed of the currently-attached cohort. `seededRef` guards
  // against later list refetches clobbering the user's subsequent picks.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!currentCohortId) return;
    if (!allOptions.some((o) => o.cohortProfileId === currentCohortId)) return;
    seededRef.current = true;
    setSelectedId(currentCohortId);
  }, [currentCohortId, allOptions]);

  const onSelect = useCallback((cohortProfileId: string) => {
    setSelectedId(cohortProfileId);
  }, []);

  const onConfirm = useCallback(() => {
    if (!selectedId) return;
    if (selectedId === currentCohortId) {
      onClose();
      return;
    }

    const selectedRow = cohortList?.find((c) => c.cohortProfileId === selectedId);
    const cohortVersionId = selectedRow?.latestCohortVersionId;
    if (cohortVersionId) {
      queryClient.prefetchQuery({
        queryKey: cohortQueryKeys.version(cohortVersionId),
        queryFn: () => fetchCohortVersion(projectId, selectedId, cohortVersionId),
      });
    }

    if (currentCohortId) removeCohort(currentCohortId);
    addCohort({ cohortProfileId: selectedId });
    onClose();
  }, [
    selectedId,
    currentCohortId,
    addCohort,
    removeCohort,
    onClose,
    cohortList,
    queryClient,
    projectId,
  ]);

  const onCancelClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const canConfirm = selectedId !== null && selectedId !== currentCohortId && !isLoading;

  const emptyMessage =
    !isLoading && !isError && allOptions.length === 0
      ? intl.formatMessage({ id: 'analyticalDatasetDetail.addCohortModal.empty' })
      : null;

  const noResultsMessage =
    !isLoading && !isError && allOptions.length > 0 && options.length === 0
      ? intl.formatMessage({ id: 'analyticalDatasetDetail.addCohortModal.noResults' })
      : null;

  const formatSubjectCount = useCallback(
    (count: number) =>
      intl.formatMessage({ id: 'analyticalDatasetDetail.addCohortModal.subjects' }, { count }),
    [intl]
  );

  const dialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText: intl.formatMessage({
      id: 'analyticalDatasetDetail.addCohortModal.addConfirm',
    }),
    entryCancelButtonText: intl.formatMessage({
      id: 'analyticalDatasetDetail.addCohortModal.cancel',
    }),
  };

  return {
    // Always the Add title — no "Replace cohort" variant.
    title: intl.formatMessage({ id: 'analyticalDatasetDetail.addCohortModal.addTitle' }),
    selectHeading: intl.formatMessage(
      { id: 'analyticalDatasetDetail.addCohortModal.selectHeading' },
      { count: options.length }
    ),
    searchPlaceholder: intl.formatMessage({
      id: 'analyticalDatasetDetail.addCohortModal.searchPlaceholder',
    }),
    emptyMessage,
    noResultsMessage,
    searchQuery,
    onSearchChange,
    options,
    selectedId,
    onSelect,
    onConfirm,
    onCancelClose,
    isLoading,
    isError,
    canConfirm,
    formatSubjectCount,
    dialogMessages,
  };
};
